import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { organizationsApi } from '@/api/organizations.api';
import { resolveCountryCode, useCurrentOrganization } from '@/hooks/useCurrentOrganization';
import { subscriptionsApi } from '@/api/subscriptions.api';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { bumpNavModules } from '@/store/appSlice';
import { setEnabledModules } from '@/store/authSlice';
import {
  readOnboardingState,
  setOnboardingDone,
  type OnboardingModule,
  type OnboardingPlan,
} from './onboardingState';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { enabledHeadModuleCodes } from '@/config/moduleEntries';
import { SampleDataPanel } from '@/components/ui/SampleDataPanel';
import { MasterLocaleSelects, countryLabel } from '@/components/master/MasterLocaleSelects';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useMasterData } from '@/hooks/useMasterData';
import { notify } from '@/core/toast/notify';

const plans: { code: OnboardingPlan; title: string; details: string }[] = [
  { code: 'FREE', title: 'Free', details: 'Limited usage with upgrade prompts' },
  { code: 'STARTER', title: 'Starter', details: 'Small team production usage' },
  { code: 'PROFESSIONAL', title: 'Professional', details: 'Growing organizations and automation' },
  { code: 'ENTERPRISE', title: 'Enterprise', details: 'Advanced controls and white-label options' },
  { code: 'CUSTOM', title: 'Custom', details: 'Tailored limits and bespoke plan' },
];

function getDashboardPath() {
  return MODULE_HUB_PATH;
}

export function OnboardingPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.auth.user);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const { countries } = useMasterData();
  const { data: org } = useCurrentOrganization(Boolean(user?.organizationId));
  const savedOnboarding = readOnboardingState();
  const [form, setForm] = useState({
    name: '',
    organizationType: 'Company',
    countryCode: 'IN',
    timezone: 'Asia/Kolkata',
    currencyCode: 'INR',
    industry: '',
    logo: '',
    themeMode: 'light',
    selectedModules: (savedOnboarding.selectedModules?.length
      ? savedOnboarding.selectedModules
      : ['POS']) as OnboardingModule[],
    plan: (savedOnboarding.selectedPlan ?? 'FREE') as OnboardingPlan,
  });

  useEffect(() => {
    if (!org) return;
    const resolvedCountry = resolveCountryCode(org, countries);
    setForm((prev) => ({
      ...prev,
      name: org.name ?? prev.name,
      countryCode: resolvedCountry || prev.countryCode,
      timezone: org.timezone ?? prev.timezone,
      currencyCode: org.currencyCode ?? prev.currencyCode,
      logo: org.logo ?? prev.logo,
      themeMode: org.themeMode ?? prev.themeMode,
    }));
  }, [org, countries.length]);

  const canWhiteLabel = form.plan === 'ENTERPRISE' || form.plan === 'CUSTOM';
  const totalSteps = 4;

  const nextDisabled = useMemo(() => {
    if (step === 1) return !form.name.trim();
    return false;
  }, [form.name, step]);

  const complete = async () => {
    if (!user?.organizationId) return;
    setSubmitting(true);
    try {
      await organizationsApi.update(user.organizationId, {
        name: form.name.trim(),
        email: user.email,
        country: countryLabel(countries, form.countryCode),
        countryCode: form.countryCode,
        timezone: form.timezone,
        currencyCode: form.currencyCode,
        logo: form.logo || undefined,
        themeMode: form.themeMode,
      });

      dispatch(setEnabledModules(enabledHeadModuleCodes(form.selectedModules)));
      dispatch(bumpNavModules());

      const availablePlans = await subscriptionsApi.listPlans();
      const mappedPlanCode =
        form.plan === 'FREE'
          ? 'FREE'
          : form.plan === 'STARTER'
            ? 'BASIC'
            : form.plan === 'PROFESSIONAL'
              ? 'PREMIUM'
              : 'ENTERPRISE';
      const selected = availablePlans.find((p) => p.planCode === mappedPlanCode);
      if (selected) {
        await subscriptionsApi.subscribe({ planId: selected.id, billingCycle: 'MONTHLY' });
      }

      setOnboardingDone(true);
      notify.success('Onboarding completed');
      navigate(getDashboardPath(), { replace: true });
    } catch {
      notify.error('Unable to complete onboarding. Please review details and retry.');
    } finally {
      setSubmitting(false);
    }
  };

  const tourContent = (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <p className="font-semibold text-slate-800 dark:text-slate-100">Guided quick tour</p>
      {form.selectedModules.includes('POS') && <p className="text-sm text-slate-600 dark:text-slate-300">POS: Create product → create customer → generate invoice</p>}
      {form.selectedModules.includes('CRM') && <p className="text-sm text-slate-600 dark:text-slate-300">CRM: Create lead → move pipeline → convert deal</p>}
      {form.selectedModules.includes('HR') && <p className="text-sm text-slate-600 dark:text-slate-300">HR: Create employee → attendance setup → payroll run</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 dark:bg-slate-950">
      <div className="mx-auto max-w-5xl rounded-3xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-800 dark:bg-slate-900 sm:p-8">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.18em] text-primary-600">Fattah onboarding</p>
          <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">Set up your organization workspace</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Step {step} of {totalSteps} — organization details, plan, branding, and guided setup.
          </p>
        </div>

        {step === 1 && (
          <div className="grid gap-4 md:grid-cols-2">
            <Input label="Organization name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            <Input label="Organization type" value={form.organizationType} onChange={(e) => setForm((p) => ({ ...p, organizationType: e.target.value }))} />
            <MasterLocaleSelects
              className="md:col-span-2"
              value={{
                countryCode: form.countryCode,
                currencyCode: form.currencyCode,
                timezone: form.timezone,
              }}
              onChange={(patch) => setForm((p) => ({ ...p, ...patch }))}
              countryRequired
              currencyRequired
              timezoneRequired
            />
            <Input label="Industry" value={form.industry} onChange={(e) => setForm((p) => ({ ...p, industry: e.target.value }))} />
          </div>
        )}

        {step === 2 && (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {plans.map((p) => (
              <button
                key={p.code}
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, plan: p.code }))}
                className={`rounded-2xl border p-4 text-left ${
                  form.plan === p.code ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30' : 'border-slate-200 dark:border-slate-700'
                }`}
              >
                <p className="font-semibold text-slate-900 dark:text-white">{p.title}</p>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{p.details}</p>
              </button>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="grid gap-4 md:grid-cols-2">
            <Input label="Logo URL" value={form.logo} onChange={(e) => setForm((p) => ({ ...p, logo: e.target.value }))} placeholder="https://..." />
            <Select
              label="Theme mode"
              value={form.themeMode}
              onChange={(v) => setForm((p) => ({ ...p, themeMode: v }))}
              options={[
                { value: 'light', label: 'Light' },
                { value: 'dark', label: 'Dark' },
              ]}
            />
            {!canWhiteLabel && (
              <p className="md:col-span-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                White-label branding is available on Enterprise and Custom plans.
              </p>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            {tourContent}
            <SampleDataPanel compact />
          </div>
        )}

        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1 || submitting}
            className="btn btn-secondary rounded-xl"
          >
            Back
          </button>
          {step < totalSteps ? (
            <button
              type="button"
              onClick={() => setStep((s) => Math.min(totalSteps, s + 1))}
              disabled={nextDisabled || submitting}
              className="btn-primary"
            >
              Continue
            </button>
          ) : (
            <button type="button" onClick={() => void complete()} disabled={submitting} className="btn-primary">
              {submitting ? 'Finishing...' : 'Finish onboarding'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
